import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AffiliateAuthProvider, useAffiliateAuth } from './context/AffiliateAuthContext';
import { SocketProvider, useSocket } from './context/SocketContext';
import FollowupReminderModal from './components/common/FollowupReminderModal';
import LeadReceivedModal from './components/common/LeadReceivedModal';
import LoginSummaryModal from './components/common/LoginSummaryModal';
import RenewalRequired from './components/common/RenewalRequired';

// Eager-load auth page
import Login from './pages/Login';

// Public pages
const Pricing = lazy(() => import('./pages/Pricing'));
const Register = lazy(() => import('./pages/Register'));
const PublicAffiliateApply = lazy(() => import('./pages/PublicAffiliateApply'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const DeleteAccountConfirm = lazy(() => import('./pages/DeleteAccountConfirm'));
const CancelDeletion = lazy(() => import('./pages/CancelDeletion'));

// Affiliate portal pages
const AffiliateLogin = lazy(() => import('./pages/affiliate/Login'));
const AffiliateForgotPassword = lazy(() => import('./pages/affiliate/ForgotPassword'));
const AffiliateResetPassword = lazy(() => import('./pages/affiliate/ResetPassword'));
const AffiliateDashboard = lazy(() => import('./pages/affiliate/Dashboard'));
const AffiliateCommissions = lazy(() => import('./pages/affiliate/Commissions'));
const AffiliatePayouts = lazy(() => import('./pages/affiliate/Payouts'));
const AffiliateRewards = lazy(() => import('./pages/affiliate/Rewards'));
const AffiliateEarningsCalculator = lazy(() => import('./pages/affiliate/EarningsCalculator'));
const AffiliateProfile = lazy(() => import('./pages/affiliate/Profile'));

// Lazy-load all other pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const LeadsBin = lazy(() => import('./pages/LeadsBin'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const TasksPipeline = lazy(() => import('./pages/TasksPipeline'));
const Followups = lazy(() => import('./pages/Followups'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Quotations = lazy(() => import('./pages/Quotations'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Payments = lazy(() => import('./pages/Payments'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Content = lazy(() => import('./pages/Content'));
const Reports = lazy(() => import('./pages/Reports'));
const TeamActivity = lazy(() => import('./pages/TeamActivity'));
const PrivacyRequests = lazy(() => import('./pages/PrivacyRequests'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Users = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));

// Owner pages
const OwnerDashboard = lazy(() => import('./pages/owner/OwnerDashboard'));
const WorkspaceList = lazy(() => import('./pages/owner/WorkspaceList'));
const WorkspaceDetail = lazy(() => import('./pages/owner/WorkspaceDetail'));
const OwnerUsers = lazy(() => import('./pages/owner/OwnerUsers'));

// Super admin pages
const SuperAdminDashboard = lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const Organizations = lazy(() => import('./pages/superadmin/Organizations'));
const OrganizationDetail = lazy(() => import('./pages/superadmin/OrganizationDetail'));
const OrganizationNew = lazy(() => import('./pages/superadmin/OrganizationNew'));
const Plans = lazy(() => import('./pages/superadmin/Plans'));
const Affiliates = lazy(() => import('./pages/superadmin/Affiliates'));
const Coupons = lazy(() => import('./pages/superadmin/Coupons'));
const PlanInvoices = lazy(() => import('./pages/superadmin/PlanInvoices'));
const SuperAdminSettings = lazy(() => import('./pages/superadmin/Settings'));

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d0d1a' }}>
    <div style={{ width: 40, height: 40, border: '3px solid #1e1e3a', borderTopColor: '#e94560', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

const SubscriptionLapsedNotice = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', textAlign: 'center', padding: 24 }}>
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Subscription expired</div>
      <div style={{ color: 'var(--text-muted, #999)' }}>Ask your account owner to renew the plan to continue using the CRM.</div>
    </div>
  </div>
);

// Guards
const PrivateRoute = ({ children, roles }) => {
  const { user, org, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;

  const isExpired = user.role !== 'superadmin' && org && org.planExpiresAt && new Date(org.planExpiresAt) < new Date() && !org.isSuspended;
  if (isExpired) {
    return user.role === 'owner' ? <RenewalRequired /> : <SubscriptionLapsedNotice />;
  }

  return children;
};

const AffiliatePrivateRoute = ({ children }) => {
  const { affiliate, loading } = useAffiliateAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!affiliate) return <Navigate to="/affiliate/login" state={{ from: location }} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) {
    if (user.role === 'superadmin') return <Navigate to="/superadmin/dashboard" replace />;
    if (user.role === 'owner') return <Navigate to="/owner/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Referral links used to point at /signup?ref=<slug>; those already-shared links now land on the
// single signup page (/register), which pre-fills the referral code from the same ?ref= value.
const SignupRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/register${location.search}`} replace />;
};

const AppRoutes = () => (
  <Suspense fallback={<Spinner />}>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/register" element={<Register />} />
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/affiliate/apply" element={<PublicAffiliateApply />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/delete-account/confirm/:token" element={<DeleteAccountConfirm />} />
      <Route path="/cancel-deletion/:token" element={<CancelDeletion />} />

      {/* Affiliate portal */}
      <Route path="/affiliate/login" element={<AffiliateLogin />} />
      <Route path="/affiliate/forgot-password" element={<AffiliateForgotPassword />} />
      <Route path="/affiliate/reset-password/:token" element={<AffiliateResetPassword />} />
      <Route path="/affiliate/dashboard" element={<AffiliatePrivateRoute><AffiliateDashboard /></AffiliatePrivateRoute>} />
      <Route path="/affiliate/commissions" element={<AffiliatePrivateRoute><AffiliateCommissions /></AffiliatePrivateRoute>} />
      <Route path="/affiliate/payouts" element={<AffiliatePrivateRoute><AffiliatePayouts /></AffiliatePrivateRoute>} />
      <Route path="/affiliate/rewards"element={<AffiliatePrivateRoute><AffiliateRewards /></AffiliatePrivateRoute>} />
      <Route path="/affiliate/earnings-calculator" element={<AffiliatePrivateRoute><AffiliateEarningsCalculator /></AffiliatePrivateRoute>} />
      <Route path="/affiliate/profile" element={<AffiliatePrivateRoute><AffiliateProfile /></AffiliatePrivateRoute>} />

      {/* Employee / Admin / Owner shared routes */}
      <Route path="/dashboard" element={<PrivateRoute roles={['admin', 'employee']}><Dashboard /></PrivateRoute>} />
      <Route path="/leads" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Leads /></PrivateRoute>} />
      <Route path="/leads/bin" element={<PrivateRoute roles={['admin', 'owner']}><LeadsBin /></PrivateRoute>} />
      <Route path="/leads/:id" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><LeadDetail /></PrivateRoute>} />
      <Route path="/pipeline" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Pipeline /></PrivateRoute>} />
      <Route path="/tasks-pipeline" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><TasksPipeline /></PrivateRoute>} />
      <Route path="/followups" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Followups /></PrivateRoute>} />
      <Route path="/appointments" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Appointments /></PrivateRoute>} />
      <Route path="/quotations" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Quotations /></PrivateRoute>} />
      <Route path="/invoices" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Invoices /></PrivateRoute>} />
      <Route path="/payments" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Payments /></PrivateRoute>} />
      <Route path="/expenses" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Expenses /></PrivateRoute>} />
      <Route path="/content" element={<PrivateRoute roles={['admin', 'employee', 'owner']}><Content /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute roles={['admin', 'owner']}><Reports /></PrivateRoute>} />
      <Route path="/team-activity" element={<PrivateRoute roles={['admin', 'owner']}><TeamActivity /></PrivateRoute>} />
      <Route path="/privacy-requests" element={<PrivateRoute roles={['admin', 'owner']}><PrivacyRequests /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute roles={['admin', 'employee']}><Notifications /></PrivateRoute>} />
      <Route path="/users" element={<PrivateRoute roles={['admin', 'owner']}><Users /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute roles={['admin', 'owner']}><Settings /></PrivateRoute>} />

      {/* Owner routes */}
      <Route path="/owner/dashboard" element={<PrivateRoute roles={['owner']}><OwnerDashboard /></PrivateRoute>} />
      <Route path="/owner/workspaces" element={<PrivateRoute roles={['owner']}><WorkspaceList /></PrivateRoute>} />
      <Route path="/owner/workspaces/:id" element={<PrivateRoute roles={['owner']}><WorkspaceDetail /></PrivateRoute>} />
      <Route path="/owner/notifications" element={<PrivateRoute roles={['owner']}><Notifications /></PrivateRoute>} />
      <Route path="/owner/users" element={<PrivateRoute roles={['owner']}><OwnerUsers /></PrivateRoute>} />
      <Route path="/owner/settings" element={<PrivateRoute roles={['owner']}><Settings /></PrivateRoute>} />

      {/* Super admin routes */}
      <Route path="/superadmin/dashboard" element={<PrivateRoute roles={['superadmin']}><SuperAdminDashboard /></PrivateRoute>} />
      <Route path="/superadmin/organizations" element={<PrivateRoute roles={['superadmin']}><Organizations /></PrivateRoute>} />
      <Route path="/superadmin/organizations/new" element={<PrivateRoute roles={['superadmin']}><OrganizationNew /></PrivateRoute>} />
      <Route path="/superadmin/organizations/:id" element={<PrivateRoute roles={['superadmin']}><OrganizationDetail /></PrivateRoute>} />
      <Route path="/superadmin/plans" element={<PrivateRoute roles={['superadmin']}><Plans /></PrivateRoute>} />
      <Route path="/superadmin/affiliates" element={<PrivateRoute roles={['superadmin']}><Affiliates /></PrivateRoute>} />
      <Route path="/superadmin/coupons" element={<PrivateRoute roles={['superadmin']}><Coupons /></PrivateRoute>} />
      <Route path="/superadmin/invoices" element={<PrivateRoute roles={['superadmin']}><PlanInvoices /></PrivateRoute>} />
      <Route path="/superadmin/settings" element={<PrivateRoute roles={['superadmin']}><SuperAdminSettings /></PrivateRoute>} />

      {/* Fallback */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  </Suspense>
);

const GlobalModals = () => {
  const { followupReminder, dismissFollowupReminder, leadReceivedAlert, dismissLeadReceivedAlert } = useSocket();
  return (
    <>
      <LoginSummaryModal />
      <FollowupReminderModal reminder={followupReminder} onDismiss={dismissFollowupReminder} />
      <LeadReceivedModal alert={leadReceivedAlert} onDismiss={dismissLeadReceivedAlert} />
    </>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AffiliateAuthProvider>
      <SocketProvider>
        <AppRoutes />
        <GlobalModals />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#1a1a2e', color: '#e2e8f0', border: '1px solid #2a2a4a', fontSize: 13 },
            success: { iconTheme: { primary: '#22c55e', secondary: '#0d0d1a' } },
            error: { iconTheme: { primary: '#e94560', secondary: '#0d0d1a' } },
          }}
        />
      </SocketProvider>
      </AffiliateAuthProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
